/* eslint-disable max-len */
import React from 'react';
import { shape, func } from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { Typography, Link } from '@one-thd/sui-atomic-components';

import WasherDryerContainer from './WasherDryerContainer';
import AccessoriesContainer from './AccessoriesContainer';
import { ACCESSORIES, DRYER, WASHER } from '../constants/index';
import LaundryProductsLayout from './LaundryProductsLayout';
import LaundryAddIcon from './LaundryAddIcon';

/**
 * LaundryBundleMiniContent component renders a section for building a matching laundry set.
 * It displays a title, a link to shop all laundry sets, container for washer and dryer products,
 * container for accessories and bundle price.
 *
 * @param {Object} props - The props object.
 * @param {Object} props.products - An object containing the washer and dryer products.
 * @param {Object} props.products.WASHER - The washer product details.
 * @param {Object} props.products.DRYER - The dryer product details.
 * @param {Object} props.products.ACCESSORIES - The accessory product details.
 * @param {Function} props.onSwapVariant - Callback function to handle swapping product variants.
 *
 * @returns {JSX.Element} The rendered LaundryBundleMiniContent component.
 */
export const LaundryBundleMiniContent = ({ products, onSwapVariant: handleSwapVariant }) => {
  const bundleAccessories = products[ACCESSORIES];
  return (
    <div data-testid="laundry-entry-point">
      <div className="sui-flex sui-flex-col md:sui-flex-row md:sui-items-center sui-gap-1 md:sui-gap-0 sui-pt-4 md:sui-pt-6 lg:sui-pt-8 sui-pb-4 sui-justify-between">
        <Typography variant="h2">Build A Matching Set</Typography>
        <Link href="#" target="_blank" variant="body-sm">Shop All Laundry Sets</Link>
      </div>
      <LaundryProductsLayout
        grid={{
          md: 12
        }}
        element="section"
      >
        <div className="md:sui-col-span-7 lg:sui-col-span-5">
          <WasherDryerContainer
            washer={products[WASHER]}
            dryer={products[DRYER]}
            onSwapVariant={handleSwapVariant}
          />
        </div>
        {
          !!bundleAccessories && (
            <>
              <LaundryAddIcon
                grid={{
                  md: 12
                }}
                className="md:sui-top-0"
                iconClassName="sui-my-6 md:sui-transform md:sui-translate-y-10 md:sui-h-[calc(300%_+_32px)] md:sui-my-0 md:sui-col-start-8 lg:sui-col-start-6 lg:md:sui-h-[calc(200%_+_32px)]"
              />
              <div className="md:sui-col-span-4 md:sui-col-start-9 lg:sui-col-span-2 lg:sui-col-start-7">
                <AccessoriesContainer product={bundleAccessories} onSwapVariant={handleSwapVariant} />
              </div>
            </>
          )
        }
      </LaundryProductsLayout>
    </div>
  );
};

LaundryBundleMiniContent.propTypes = {
  products: shape({}).isRequired,
  onSwapVariant: func.isRequired
};

LaundryBundleMiniContent.dataModel = extend(
  WasherDryerContainer,
  AccessoriesContainer
);